'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import Link from 'next/link';
import { useState } from 'react';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { earnStatisticsBunja } from '@flipster/features/src/shared/earn/state';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

const CumulativeEarnings = () => {
  const { isCookieLoggedIn } = useAuth();
  const { earnStatisticsAtom } = useBunja(earnStatisticsBunja);
  const earnStatistics = useAtomValue(earnStatisticsAtom);

  const cumulativeEarningsEarnProgram = Num(earnStatistics?.cumulativeEarningsEarnProgram ?? 0);
  const cumulativeEarningsEarnPool = Num(earnStatistics?.cumulativeEarningsEarnPool ?? 0);
  const cumulativeEarnings = cumulativeEarningsEarnProgram.plus(cumulativeEarningsEarnPool);

  const [isClosed, setIsClosed] = useState(true);

  const handleToggle = (event: React.ToggleEvent<HTMLDetailsElement>) => {
    const newState = !event.currentTarget.open;
    setIsClosed(newState);
  };

  return (
    <details open={!isClosed} onToggle={handleToggle}>
      <summary className={flex({ align: 'center', justify: 'space-between', cursor: 'pointer' })}>
        <span
          className={css({
            textStyle: 'Text/M/Regular',
            display: 'flex',
            alignItems: 'center',
            color: 'content.default.level-5',
          })}
        >
          <FormattedMessage id='Autogen_common.r251126.s_Cumulative_earnings' />
        </span>
        <div className={flex({ align: 'center', gap: 4, color: 'content.default.level-5' })}>
          <Link href='/history/earn' prefetch={isCookieLoggedIn} className={css({ color: 'content.default.level-5' })}>
            <Formatted.Number
              value={cumulativeEarnings}
              precision={UsdtPrecision}
              toFixed
              css={{ textStyle: 'Text/L/Underline' }}
            />
          </Link>
          <span className={css({ textStyle: 'Text/M/Regular' })}>USDT</span>
          <ChevronDownIcon
            size={20}
            color={token('colors.content.default.level-3')}
            className={css({ '[open] &': { transform: 'rotate(-180deg)' } })}
          />
        </div>
      </summary>
      <div
        className={stack({
          bg: 'background.default.level-0',
          mt: 4,
          p: '8px 12px',
          rounded: 8,
          textStyle: 'Text/M/Regular',
          gap: 10,
        })}
      >
        <div className={hstack({ justify: 'space-between' })}>
          <span className={css({ color: 'content.default.level-3' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Earn' />
          </span>
          <Formatted.Number
            value={cumulativeEarningsEarnProgram}
            precision={UsdtPrecision}
            withColor
            withPlus
            toFixed
            css={{ textStyle: 'Text/M/Regular' }}
            suffix={<span className={css({ color: 'content.default.level-2' })}>{UsdtCurrency}</span>}
          />
        </div>
        <div className={hstack({ justify: 'space-between' })}>
          <span className={css({ color: 'content.default.level-3' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Earn_Pool' />
          </span>
          <Formatted.Number
            value={cumulativeEarningsEarnPool}
            precision={UsdtPrecision}
            withColor
            withPlus
            toFixed
            css={{ textStyle: 'Text/M/Regular' }}
            suffix={<span className={css({ color: 'content.default.level-2' })}>{UsdtCurrency}</span>}
          />
        </div>
      </div>
    </details>
  );
};

export default CumulativeEarnings;
